import { Box, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAGS_STATUS, KOSTEN_ARTEN } from 'src/lib/constants/constant';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import { getFieldNamefromDataPath } from 'src/utils/helper';

// import schemas
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
const MA_P = MittelabrufSchema.attributes;
const KP_P = KostenPositionSchema.attributes;
const BJ_P = MA_P.Foerderantrag.schema.attributes.Bewilligung_Jahresaufteilung.schema.attributes;

type YearlyMap = Record<number, number>;

const toNumber = (value: any) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const sumYearly = (data: YearlyMap) => Object.values(data).reduce((acc, curr) => acc + curr, 0);

const YearValues = ({
	years,
	valuesByYear,
	total,
	totalBold,
	formatter = (v: number) => DisplayEuro(v),
}: {
	years: number[];
	valuesByYear: YearlyMap;
	total: number;
	totalBold?: boolean;
	formatter?: (value: number) => string;
}) => {
	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: `repeat(${years.length + 1}, minmax(90px, auto))`,
				justifyContent: 'end',
				gap: 1,
			}}
		>
			{years.map((year) => (
				<Typography key={`value-${year}`} sx={{ textAlign: 'right' }} variant='body2'>
					{formatter(valuesByYear[year] || 0)}
				</Typography>
			))}
			<Typography sx={{ textAlign: 'right', fontWeight: totalBold ? 600 : 400 }} variant='body2'>
				{formatter(total)}
			</Typography>
		</Box>
	);
};

const BescheidDatenMAEpz: FC<{}> = () => {
	const { application } = useApplication();

	const years = useMemo(() => {
		const projectStartYear = toNumber(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Projektstart_Jahr.path, 0));
		const projectDuration = toNumber(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Projektdauerin_Jahren.path, 0));

		const yearsFromDuration =
			projectStartYear > 0 && projectDuration > 0 ? Array.from({ length: projectDuration }, (_, index) => projectStartYear + index) : [];

		return Array.from(new Set([...yearsFromDuration])).sort((a, b) => a - b);
	}, [application]);

	const showYearSplit = years.length > 1;
	const summary = useMemo(() => {
		const allKostenPositionen = get(application, MA_P.Foerderantrag.schema.attributes.Foerderantrag_Kostenpositions.path, []);
		const bewilligungJahresaufteilung = get(application, MA_P.Foerderantrag.schema.attributes.Bewilligung_Jahresaufteilung.path, []);

		const getKostenart = (ele: any) =>
			get(
				ele,
				KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Kostenart_Finanzierung.path,
				get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path, ''),
			);

		const getKostenByYear = (kostenArt: string) => {
			return allKostenPositionen
				.filter((ele: any) => getKostenart(ele) === kostenArt)
				.reduce((acc: YearlyMap, curr: any) => {
					const kostenProJahr = get(curr, KP_P.Allgemein_Kosten_Pro_Jahr.path, []);
					kostenProJahr.forEach((eintrag: any) => {
						const year = toNumber(get(eintrag, 'Zelle_1', 0));
						if (year <= 0) return;
						acc[year] = (acc[year] || 0) + toNumber(get(eintrag, 'Zelle_2', 0));
					});
					return acc;
				}, {});
		};

		const personalByYear = getKostenByYear(KOSTEN_ARTEN.Personalkosten);
		const sachByYear = getKostenByYear(KOSTEN_ARTEN.Sachkosten);
		const verwaltungByYear = getKostenByYear(KOSTEN_ARTEN.Verwaltungskostenpauschale);
		const eigenmittelByYear = getKostenByYear(KOSTEN_ARTEN.Eigenmittel);
		const gesamtByYear = years.reduce((acc: YearlyMap, year) => {
			acc[year] = (personalByYear[year] || 0) + (sachByYear[year] || 0) + (verwaltungByYear[year] || 0);
			return acc;
		}, {});

		const bewilligteFoerderungByYear = bewilligungJahresaufteilung.reduce((acc: YearlyMap, curr: any) => {
			const year = toNumber(get(curr, getFieldNamefromDataPath(BJ_P.Projektjahr.path), 0));
			if (year <= 0) return acc;
			const zuwendung = toNumber(get(curr, getFieldNamefromDataPath(BJ_P.Zuwendung.path), 0));
			acc[year] = (acc[year] || 0) + zuwendung;
			return acc;
		}, {});
		const allMittelabrufe = get(application, MA_P.Foerderantrag.schema.attributes.Foerderantrag_Mittelabrufs.path, []);
		const bereitsAbgerufenByYear = allMittelabrufe.reduce((acc: YearlyMap, curr: any) => {
			if (get(curr, MA_P.Mittelabrufstatus_Mittelabrufstatus.path, '') === ANTRAGS_STATUS.abgelehnt) return acc;
			const year = toNumber(get(curr, MA_P.Allgemein_Haushaltsjahr.path, 0));
			if (year <= 0) return acc;
			acc[year] = (acc[year] || 0) + toNumber(get(curr, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path, 0));
			return acc;
		}, {});
		const bereitsAbgerufeneZuwendung = toNumber(get(application, MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, 0));
		const haushaltsjahr = toNumber(get(application, MA_P.Allgemein_Haushaltsjahr.path, 0));
		const verfuegbareFoerderungByYear = years.reduce((acc: YearlyMap, year) => {
			const base = bewilligteFoerderungByYear[year] || 0;
			if (year === haushaltsjahr) {
				acc[year] = Math.max(0, base - bereitsAbgerufeneZuwendung);
			} else {
				acc[year] = Math.max(0, base - (bereitsAbgerufenByYear[year] || 0));
			}
			return acc;
		}, {});

		const summePersonalAusgaben = allKostenPositionen
			.filter((ele: any) => getKostenart(ele) === KOSTEN_ARTEN.Personalkosten)
			.reduce((acc: number, curr: any) => acc + toNumber(get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0)), 0);

		const summeSachausgaben = allKostenPositionen
			.filter((ele: any) => getKostenart(ele) === KOSTEN_ARTEN.Sachkosten)
			.reduce((acc: number, curr: any) => acc + toNumber(get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0)), 0);

		const summeVerwaltungspauschale = allKostenPositionen
			.filter((ele: any) => getKostenart(ele) === KOSTEN_ARTEN.Verwaltungskostenpauschale)
			.reduce((acc: number, curr: any) => acc + toNumber(get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0)), 0);

		const eigenmittelGesamt = toNumber(
			get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path, sumYearly(eigenmittelByYear)),
		);

		const bewilligteFoerderungGesamt = toNumber(
			get(application, MA_P.Foerderantrag.schema.attributes.Bewilligung_Bewilligte_Zuwendung.path, sumYearly(bewilligteFoerderungByYear)),
		);

		const verfuegbareFoerderungGesamt = Math.max(0, bewilligteFoerderungGesamt - bereitsAbgerufeneZuwendung);

		const foerderquoteByYear = bewilligungJahresaufteilung.reduce((acc: YearlyMap, curr: any) => {
			const year = toNumber(get(curr, getFieldNamefromDataPath(BJ_P.Projektjahr.path), 0));
			if (year <= 0) return acc;
			acc[year] = toNumber(get(curr, getFieldNamefromDataPath(BJ_P.Foerderquote_403741.path), 0));
			return acc;
		}, {});

		return {
			totals: {
				[KOSTEN_ARTEN.Personalkosten]: summePersonalAusgaben,
				[KOSTEN_ARTEN.Sachkosten]: summeSachausgaben,
				[KOSTEN_ARTEN.Verwaltungskostenpauschale]: summeVerwaltungspauschale,
				gesamt: toNumber(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Geplante_Projektausgaben.path, sumYearly(gesamtByYear))),
				[KOSTEN_ARTEN.Eigenmittel]: eigenmittelGesamt,
				bewilligteFoerderungGesamt,
				verfuegbareFoerderungGesamt,
			},
			byYear: {
				personalByYear,
				sachByYear,
				verwaltungByYear,
				gesamtByYear,
				eigenmittelByYear,
				bewilligteFoerderungByYear,
				verfuegbareFoerderungByYear,
				foerderquoteByYear,
			},
		};
	}, [application, years]);

	return (
		<>
			<ApplicationHeading title={'Übersicht Bewilligung'} />
			<Typography>Folgende Projektdaten sind lt. Bescheid bewilligt und zu berücksichtigen:</Typography>

			<Box sx={{ mt: '2rem' }}>
				{showYearSplit ?
					<Box sx={{ overflowX: 'auto' }}>
						<Box sx={{ minWidth: `${420 + years.length * 100}px` }}>
							<SummaryLine
								label={''}
								value={
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: `repeat(${years.length + 1}, minmax(90px, auto))`,
											justifyContent: 'end',
											gap: 1,
										}}
									>
										{years.map((year) => (
											<Typography key={`header-${year}`} sx={{ textAlign: 'right', fontWeight: 700 }} variant='body2'>
												{year}
											</Typography>
										))}
										<Typography sx={{ textAlign: 'right', fontWeight: 700 }} variant='body2'>
											Gesamt
										</Typography>
									</Box>
								}
							/>

							<SummaryLine
								label={'Zuwendungsfähige Gesamtausgaben bis zu:'}
								alignValueRight
								boldLabel={true}
								value={<YearValues years={years} valuesByYear={summary.byYear.gesamtByYear} total={summary.totals.gesamt} totalBold={true} />}
							/>

							<Typography sx={{ paddingLeft: '0.5rem', mt: '1rem' }}>davon</Typography>

							<SummaryLine
								alignValueRight
								labelPadding='0.75rem 2rem'
								label={'Personalausgaben bis zu:'}
								value={
									<YearValues
										years={years}
										valuesByYear={summary.byYear.personalByYear}
										total={summary.totals[KOSTEN_ARTEN.Personalkosten]}
									/>
								}
							/>
							<SummaryLine
								alignValueRight
								labelPadding='0.75rem 2rem'
								label={'Sachausgaben bis zu:'}
								value={<YearValues years={years} valuesByYear={summary.byYear.sachByYear} total={summary.totals[KOSTEN_ARTEN.Sachkosten]} />}
							/>
							<SummaryLine
								alignValueRight
								labelPadding='0.75rem 2rem'
								label={'Verwaltungpauschale bis zu:'}
								value={
									<YearValues
										years={years}
										valuesByYear={summary.byYear.verwaltungByYear}
										total={summary.totals[KOSTEN_ARTEN.Verwaltungskostenpauschale]}
									/>
								}
							/>

							<SummaryLine
								alignValueRight
								labelPadding='0.75rem 2rem'
								label={'Eigenmittel gesamt:'}
								value={
									<YearValues
										years={years}
										valuesByYear={summary.byYear.eigenmittelByYear}
										total={summary.totals[KOSTEN_ARTEN.Eigenmittel]}
									/>
								}
							/>

							<Box sx={{ mt: '2rem' }}>
								<SummaryLine
									alignValueRight
									label={'Förderquote: '}
									boldLabel={true}
									value={
										<YearValues
											years={years}
											valuesByYear={summary.byYear.foerderquoteByYear}
											total={toNumber(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0))}
											totalBold={true}
											formatter={(v) => DisplayDecimal(v, true)}
										/>
									}
								/>
								<SummaryLine
									alignValueRight
									label={'Bewilligte Förderung: '}
									boldLabel={true}
									value={
										<YearValues
											years={years}
											valuesByYear={summary.byYear.bewilligteFoerderungByYear}
											total={summary.totals.bewilligteFoerderungGesamt}
											totalBold={true}
										/>
									}
								/>
								<SummaryLine
									alignValueRight
									label={'Noch verfügbare Fördermittel: '}
									value={
										<YearValues
											years={years}
											valuesByYear={summary.byYear.verfuegbareFoerderungByYear}
											total={summary.totals.verfuegbareFoerderungGesamt}
										/>
									}
								/>
							</Box>
						</Box>
					</Box>
				:	<>
						<SummaryLine
							label={'Zuwendungsfähige Gesamtausgaben bis zu:'}
							alignValueRight
							boldLabel={true}
							value={DisplayEuro(summary.totals.gesamt)}
						/>

						<Typography sx={{ paddingLeft: '0.5rem', mt: '1rem' }}>davon</Typography>

						<SummaryLine
							alignValueRight
							labelPadding='0.75rem 2rem'
							label={'Personalausgaben bis zu:'}
							value={DisplayEuro(summary.totals[KOSTEN_ARTEN.Personalkosten])}
							valueFontWeight={400}
						/>
						<SummaryLine
							alignValueRight
							labelPadding='0.75rem 2rem'
							label={'Sachausgaben bis zu:'}
							value={DisplayEuro(summary.totals[KOSTEN_ARTEN.Sachkosten])}
							valueFontWeight={400}
						/>
						<SummaryLine
							alignValueRight
							labelPadding='0.75rem 2rem'
							label={'Verwaltungpauschale bis zu:'}
							value={DisplayEuro(summary.totals[KOSTEN_ARTEN.Verwaltungskostenpauschale])}
							valueFontWeight={400}
						/>

						<SummaryLine
							alignValueRight
							labelPadding='0.75rem 2rem'
							label={'Eigenmittel gesamt:'}
							value={DisplayEuro(summary.totals[KOSTEN_ARTEN.Eigenmittel])}
						/>

						<Box sx={{ mt: '2rem' }}>
							<SummaryLine
								alignValueRight
								label={'Förderquote: '}
								boldLabel={true}
								value={DisplayDecimal(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0), true)}
							/>
							<SummaryLine
								alignValueRight
								label={'Bewilligte Förderung: '}
								boldLabel={true}
								value={DisplayEuro(summary.totals.bewilligteFoerderungGesamt)}
							/>
							<SummaryLine
								alignValueRight
								label={'Noch verfügbare Fördermittel: '}
								value={DisplayEuro(summary.totals.verfuegbareFoerderungGesamt)}
								valueFontWeight={400}
							/>
						</Box>
					</>
				}
				<Box sx={{ mt: '4rem' }}>
					<Typography>Bitte beachten Sie die Informationen zur Erstellung des Mittelabrufs im Menüpunkt „Wichtige Hinweise“.</Typography>
				</Box>
			</Box>
		</>
	);
};

export default BescheidDatenMAEpz;
